import {
  OrderResponseDto,
  OrderStatus,
  SealsDto,
  StowagePositionDto,
} from '@tom-ui/planning/app/api'
import { ContainerJourneyStatus } from '@tom-ui/planning/components/molecules/ContainerJourneyStatusChip'
import { ContainerJourneyItemStore } from '@tom-ui/planning/pages/Issues/Stores/ContainerJourneyItemStore'
import { IOrderItem } from '@tom-ui/planning/rt-stores/order/OrderItem'
import { IEvent, MessageBus } from '@tom-ui/utils'
import {
  EventTypes,
  occupancyService,
  OpenJourneyPositionsResponse,
  ReservationResponse,
  serializeBlockPositions,
  StackedInResponse,
  YardBlockStore,
} from '@tom-ui/yard'
import _ from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

export interface ContainerTurnoverListTableColumnsDto {
  id: number
  number?: string | null
  archetype?: string | null
  category?: string | null
  shippingLine?: string | null
  forwarder?: string | null
  cargoStatus?: string | null
  grossWeight: number
  imoClass: string
  oog: string
  cooling: string
  hold: string[]
  status?: string | null
  approved: string

  containerTare?: number | null
  notes?: string | null
  operationalInstructions?: string | null

  originalOrder: OrderResponseDto
  originPosition?: StowagePositionDto | null
  actualPosition?: StowagePositionDto | null

  containerJourneyId?: number | null
  inboundStatus?: OrderStatus | null

  numericReleaseReference?: string | null
  nonNumericReleaseReference?: string | null
  consignee?: string | null
  pol?: string | null
  sealNo?: string | null
  documentNo?: string | null
  bl?: string | null
  inlandDest?: string | null
  plannedYardLocation?: string | null
  currentYardLocation?: string | null
}

export class ContainerTurnoverListViewStore {
  selectedJourneyId?: number
  selectedContainerNumber?: string | null
  isReservationDialogOpen = false
  isUpdatePositionDialogOpen = false
  openJourneyPositions: OpenJourneyPositionsResponse[] = []

  constructor(
    private readonly containerJourneyItemStore: ContainerJourneyItemStore,
    private readonly yardBlockStore: YardBlockStore,
    private readonly yardMessageBus: MessageBus,
  ) {
    makeObservable(this, {
      selectedJourneyId: observable,
      selectedContainerNumber: observable,
      isReservationDialogOpen: observable,
      isUpdatePositionDialogOpen: observable,
      openJourneyPositions: observable,

      toggleReservationDialog: action,
      toggleUpdatePositionDialog: action,

      ordersTableData: computed,
    })

    this.yardMessageBus.subscribeEvent<StackedInResponse[]>(
      EventTypes.StackedInEvent,
      this.handleStackedInEvent,
    )

    this.yardMessageBus.subscribeEvent<ReservationResponse[]>(
      EventTypes.CreatedReservationEvent,
      this.handleCreatedReservationEvent,
    )
  }

  fetchOpenJourneyPositions = async () => {
    const response = await occupancyService.openJourneyPositions()

    runInAction(() => {
      this.openJourneyPositions = response
    })
  }

  toggleReservationDialog = (journeyId?: number, containerNumber?: string | null) => {
    this.isReservationDialogOpen = !this.isReservationDialogOpen
    this.selectedJourneyId = journeyId
    this.selectedContainerNumber = containerNumber
  }

  toggleUpdatePositionDialog = (journeyId?: number, containerNumber?: string | null) => {
    this.isUpdatePositionDialogOpen = !this.isUpdatePositionDialogOpen
    this.selectedJourneyId = journeyId
    this.selectedContainerNumber = containerNumber
  }

  mapToTableData(order: IOrderItem): ContainerTurnoverListTableColumnsDto {
    return {
      id: order.id,
      number: order.data.containerNumber,
      archetype: order.data.isoCodeArchetype,
      category: order.journeyCategory,
      shippingLine: order.data.customerName,
      forwarder: order.forwarder?.data.name,
      cargoStatus: order.data.isEmpty ? 'Empty' : 'Full',
      grossWeight: order.data.grossWeight ?? 0,
      imoClass:
        Array.isArray(order.data.imoClasses) && order.data.imoClasses.length > 0
          ? order.data.imoClasses.join(', ')
          : 'No',
      oog: order.data.isOOG ? 'Yes' : 'No',
      cooling: order.containerJourney?.data.coolingOrderId ? 'Yes' : 'No',
      hold: order.activeHoldNames,
      status: order.containerJourney?.status,
      approved: order.data.isApproved ? 'Yes' : 'No',

      containerTare: order.data.containerTare ?? 0,
      notes: order.data.notes,
      operationalInstructions: order.data.operationalInstructions,
      actualPosition: order.data.actualStowagePosition,
      originalOrder: order.data,
      numericReleaseReference: order.data.numericReleaseReference,
      nonNumericReleaseReference: order.data.nonNumericReleaseReference,

      inboundStatus: order.data.status,
      consignee: order.data.consignee,
      pol: order.data.portOfLoading,
      sealNo:
        Array.isArray(order.data.seals) && order.data.seals.length > 0
          ? order.data.seals
              .map((s: SealsDto) => s?.number ?? '')
              .filter(Boolean)
              .join(', ')
          : order.data.hasSeals
            ? 'Yes'
            : 'No',
      documentNo: order.data.releaseReference,
      bl: order.data.billOfLading,
      originPosition: order.data.stowagePosition,
      inlandDest: order.data.inlandDestination,
      plannedYardLocation: this.getPlannedPositionSerialized(order.containerJourney?.data.id),
      currentYardLocation: this.getCurrentYardLocationSerialized(order.containerJourney?.data.id),
      containerJourneyId: order.containerJourney?.data.id,
    }
  }

  get ordersTableData(): ContainerTurnoverListTableColumnsDto[] {
    return (
      this.containerJourneyItemStore.items
        .filter(journey => journey.status !== ContainerJourneyStatus.Departed)
        .map(journey =>
          journey.outboundOrder && journey.outboundOrder.visit //it uses the same logic as SearchContainerJourneyVisit to select the correct journey
            ? journey.outboundOrder
            : journey.inboundOrder,
        )
        .filter(o => !!o)
        .map(o => this.mapToTableData(o!)) ?? []
    )
  }

  getPlannedPositionSerialized = (journeyId: number | undefined): string | null => {
    const journeyPositions = this.findJourneyPositions(journeyId)
    if (!journeyPositions) return null

    return serializeBlockPositions(
      this.yardBlockStore.yardBlocks,
      journeyPositions.reservations,
      journeyPositions.plannedBlockId,
    )
  }

  getCurrentYardLocationSerialized = (journeyId: number | undefined): string | null => {
    const journeyPositions = this.findJourneyPositions(journeyId)
    if (!journeyPositions) return null

    return serializeBlockPositions(this.yardBlockStore.yardBlocks, journeyPositions.occupancies)
  }

  private readonly findJourneyPositions = (journeyId: number | undefined) => {
    if (!journeyId) return null
    return this.openJourneyPositions.find(x => x.journeyId === journeyId) ?? null
  }

  private readonly handleStackedInEvent = (event: IEvent<StackedInResponse[]>) => {
    const stackedInResponses = event.payload

    if (!stackedInResponses) return

    //remove reservations for positions that were stacked in
    const journeyIds = new Set(stackedInResponses.map(r => r.journeyId))
    const existings = this.openJourneyPositions.filter(pos => journeyIds.has(pos.journeyId))
    if (existings) {
      runInAction(() => {
        existings.forEach(existing => {
          existing.reservations = []
        })
      })
    }

    this.updateOpenJourneys(stackedInResponses, 'occupancies')
  }

  private readonly handleCreatedReservationEvent = (event: IEvent<ReservationResponse[]>) => {
    const reservationResponses = event.payload

    if (!reservationResponses) return

    this.updateOpenJourneys(reservationResponses, 'reservations')
  }

  private readonly updateOpenJourneys = (
    responses: StackedInResponse[] | ReservationResponse[],
    positionType: 'occupancies' | 'reservations',
  ) => {
    const groupedByJourneyId = _.groupBy(responses, item => item.journeyId)

    runInAction(() => {
      Object.entries(groupedByJourneyId).forEach(([journeyId, positions]) => {
        const existing = this.openJourneyPositions.find(pos => pos.journeyId === Number(journeyId))
        if (existing) {
          existing[positionType] = positions.map(pos => ({
            blockId: pos.blockId,
            bay: pos.position?.bay,
            row: pos.position?.row,
            tier: pos.position?.tier,
          }))
        }
      })
    })
  }
}
